import React, { useState } from 'react';

import { $getSelection, LexicalEditor } from 'lexical';
import { useTheme } from '@mui/material';

import { Box } from '../../../../../Box';
import { Typography } from '../../../../../Typography';
import { DropdownButton } from '../../../../../DropdownButton';
import { Icon } from '../../../../../Icon';
import { MenuItem } from '../../../../../MenuItem';
import { Button } from '../../../../../Button';

import {
  arrowSymbols,
  currencySymbols,
  latinSymbols,
  mathematicalSymbols,
  textSymbols,
} from './symbols';

type ChapterTypeToBlockName = {
  all: string;
  currrency: string;
  text: string;
  mathematical: string;
  arrow: string;
  latin: string;
};

const chapters = {
  all: 'Все',
  currrency: 'Валюта',
  text: 'Текст',
  mathematical: 'Математические',
  arrow: 'Стрелки',
  latin: 'Латинские',
};

export type ChapterTypeKeys = keyof ChapterTypeToBlockName;

const allSymbols = [
  ...currencySymbols,
  ...textSymbols,
  ...mathematicalSymbols,
  ...arrowSymbols,
  ...latinSymbols,
];

const getCurrentSymbols = (type: ChapterTypeKeys) => {
  switch (type) {
    case 'all':
      return allSymbols;
    case 'currrency':
      return currencySymbols;
    case 'text':
      return textSymbols;
    case 'mathematical':
      return mathematicalSymbols;
    case 'arrow':
      return arrowSymbols;
    case 'latin':
      return latinSymbols;

    default:
      return allSymbols;
  }
};

export const useSymbols = (editor: LexicalEditor) => {
  const theme = useTheme();
  const [activeChapters, setActiveChapters] = useState<ChapterTypeKeys>('all');

  const handleFormat = (symbol: string) => {
    editor.update(() => {
      const selection = $getSelection();

      selection?.insertRawText(symbol);
    });
  };

  const handleClick = (key: string) => {
    setActiveChapters(() => key as ChapterTypeKeys);
  };

  return (
    <Box maxWidth="312px" maxHeight="248px" px="12" overflow="auto">
      <Box display="flex" flexDirection="column" position="relative">
        <Box
          position="sticky"
          top="0"
          left="0"
          bgcolor="bg.light.primary"
          boxShadow={`0 0 10px ${theme.tokens.colors.bg.light.primary}`}
        >
          <DropdownButton
            size="medium"
            menuItems={Object.entries(chapters).map(([chapter, name]) => (
              <MenuItem onClick={() => handleClick(chapter)} key={chapter}>
                <Typography variant="subtitle2">{name}</Typography>
              </MenuItem>
            ))}
            variant="tertiaryLight"
            verticalPosition="bottom"
            horizontalPosition="left"
            button={({ onClick }) => (
              <Box display="flex" alignItems="center">
                <Button
                  fullWidth={false}
                  variant="tertiaryLight"
                  size="medium"
                  onClick={onClick}
                >
                  <Box display="flex" alignItems="center" gap="4">
                    <Typography variant="subtitle2">
                      {chapters[activeChapters]}
                    </Typography>

                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="9px"
                    >
                      <Icon
                        name="dropdown"
                        size="16"
                        color="text.light.primary"
                      />
                    </Box>
                  </Box>
                </Button>
              </Box>
            )}
          />
        </Box>

        <Box display="flex" flexWrap="wrap">
          {getCurrentSymbols(activeChapters).map((symbol, index) => (
            <Box
              key={index}
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="40px"
              height="32px"
              border="1px solid"
              borderColor="stroke.primary"
              radius="m"
              onClick={() => handleFormat(symbol)}
              cursor="pointer"
              margin="4"
            >
              <Typography variant="body3">{symbol}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
